import { DateUtils, makeLink } from "simple-utils"
import { Account, BalanceKind, balanceKind, goalStatus, isGoogle, overrunStatus } from "../../models/Account"
import { FullReportEntry, missingInfoDayCount, ReportEntry } from "../../models/FullReportEntry"
import { getRegion } from "../../utils/state-utils"
import { inList } from "../../utils/utils"
import type { AccountAction, AccountContextState } from "../AccountContext"
import { AccountTableView } from "../AccountTableView"
import { catFilters } from "./category-filters"
import { clusterFilter } from "./cluster-filters"
import { FilterAction } from "./FilterAction"


export interface IFilter<T>{
    context: T
    id: string 
    filter(account:FullReportEntry):boolean
}

export type GoalFilter =  null | "Acima" | "Abaixo" | "Muito Abaixo" | "Abaixo Geral"
export type OverrunFilter =  null | "Alto" | "Baixo" | "Alto ou Baixo" | "Neutro"

export function checkGoal(account:ReportEntry, filter:GoalFilter){
    const goal = goalStatus(account);
    if(filter == "Abaixo Geral")
        return goal.match(/abaixo/i) != null;
    return goal == filter;
}

export function checkOverrun(account:ReportEntry, filter:OverrunFilter){
    var st = overrunStatus(account);
    if(filter == "Neutro") return st == "Neutro";
    if(st == "Neutro") return false;
    if(filter == "Alto ou Baixo") return true;
    return  st == filter;
}

export interface FilterState {
    uf?: string
    region?: string
    filter?: string
    group?: string
    consultant?: string
    goal?: GoalFilter
    overrun?: OverrunFilter
    manager?: string
    franchise?: string
    filterPattern?: string
    badStatus?: boolean
    badBalance?: boolean
    todayStatus?: boolean
    missingDays?:boolean
    noInfo?: boolean
    worseThanPastMonth?: boolean
    category?: string
    cluster?: string
    tags?: number[]
}

function makeSearchPattern(account: Account){
    return makeLink(account.name);
}

export function filterReducer(state: FilterState, action: AccountAction):FilterState {
    switch(action.type){
        case FilterAction.setFranchise : return {...state, franchise: action.payload, uf: null}
        case FilterAction.setRegion : return {...state, region: action.payload, uf: null}
        case FilterAction.setUf : return {...state, uf: action.payload, region: null}
        case FilterAction.setFilter: return {...state, filter: action.payload, filterPattern: makeLink(action.payload)}
        case FilterAction.todayStatus : return {...state, todayStatus: action.payload}
        case FilterAction.noInfo : return {...state, noInfo: action.payload}
        case FilterAction.worseThanPastMonth : return {...state, worseThanPastMonth: action.payload}
        case FilterAction.missingDays : return {...state, missingDays: action.payload}
        case FilterAction.badStatus : return {...state, badStatus: action.payload}
        case FilterAction.badBalance : return {...state, badBalance: action.payload}
        case FilterAction.setGroup : return {...state, group: action.payload}
        case FilterAction.setConsultant : return {...state, consultant: action.payload}
        case FilterAction.setGoal : return {...state, goal: action.payload}
        case FilterAction.setOverrun : return {...state, overrun: action.payload}
        case FilterAction.setManager : return {...state, manager: action.payload}
        case FilterAction.setCategory : return {...state, category: action.payload}
        case FilterAction.setCluster : return {...state, cluster: action.payload}
        case FilterAction.setTagFilter : return {...state, tags: action.payload}
        case FilterAction.clearFilters : return null
    }

    return state;
}

function tagFilter(tags:number[]):(a:FullReportEntry) => boolean{
    if(tags?.length == 0) return () => true;
    if(tags?.length == 1) return (a:FullReportEntry) => (a.tags ?? []).includes(tags[0]);
    return (a:FullReportEntry) => a.tags?.some(id => tags.includes(id))
}

export function applyFilters(accounts: FullReportEntry[], filters:FilterState):FullReportEntry[]{
    if(isEmptyFilter(filters)) return accounts;
    
    const tagFilterFn = tagFilter(filters.tags);
    
    return accounts.filter(a => {
        if(filters.filterPattern){
           if(filters.filterPattern.match(/^\d+$/))
                return a.metaId == filters.filterPattern;
           if(!makeSearchPattern(a).includes(filters.filterPattern)) return false;
        }
        if(filters.region) {
            if(getRegion(a.state) != filters.region) return false;
        }
        if(filters.uf) {
            if(a.state != filters.uf) return false;
        }

        if(filters.franchise) {
            if(a.franchise + "" != filters.franchise) return false;
        }

        if(filters.group) {
            if(!inList(a.group, filters.group)) return false;
        }
        
        if(filters.consultant) {
            if(filters.consultant != a.consultant) return false;
        }

        if(filters.goal) {
            if(!checkGoal(a, filters.goal)) return false;
        }

        if(filters.overrun) {
            if(!checkOverrun(a, filters.overrun)) return false;
        }
        
        if(filters.manager) {
            if(!inList(a.manager, filters.manager)) return false;
        }

        if(filters.todayStatus) {
            if(!a.changeStatusToday) return false;
        }

        if(filters.noInfo) {
            if(!noEntryToday(a)) return false;
        }

        if(filters.worseThanPastMonth) {
            if((a.yesterDayInfo.leads - a.pastMonthYesterDayInfo.leads) > 0) 
                return false;
        }

        if(filters.missingDays) {
            if(a.missingDays == 0) return false;
        }

        if(filters.badStatus) {
            if(a.status == 0) return false;
        }

        if(filters.badBalance) {
            let kind = balanceKind(a)
            if(kind == BalanceKind.danger || kind == BalanceKind.warning) return true;
            return false;
        }

        if(filters.category) {
            return catFilters[filters.category](a);
        }

        if(filters.cluster) {
            return clusterFilter[filters.cluster](a);
        }

        if(filters.tags?.length) {
            if(!tagFilterFn(a)) return false;
        }

        return true;
    });
}

export function isEmptyFilter(filters: FilterState){
    if(filters == null) return true;
    if(filters.filterPattern) return false;
    if(filters.uf) return false;
    if(filters.region) return false;
    if(filters.todayStatus) return false;
    if(filters.noInfo) return false;
    if(filters.worseThanPastMonth) return false;
    if(filters.missingDays) return false;
    if(filters.badStatus) return false;
    if(filters.franchise) return false;
    if(filters.group) return false;
    if(filters.consultant) return false;
    if(filters.manager) return false;
    if(filters.badBalance) return false;
    if(filters.goal) return false;
    if(filters.overrun) return false;
    if(filters.category) return false;
    if(filters.cluster) return false;
    if(filters.tags?.length) return false;
    return true;
}

const today = DateUtils.cloneDate(new Date()).getTime();

export function noEntryToday(account:FullReportEntry){
    //if(account.dailyReports.length == 0) return true;
    //var entry = account.dailyReports[account.dailyReports.length - 1];
    //return entry.date.getTime() != today;
    if(isGoogle(account))
        return account.todayCost == 0 || missingInfoDayCount(account) > 0;
    return account.todayLeads == 0 || missingInfoDayCount(account) > 0
}
